import { useState } from 'react';
import { TENANT_ROLES, type PortalUser, type TenantRole } from '@provalia/shared';
import { ErrorBox, Modal, useToast } from '../../../shared/ui';
import { api, tenantPath } from '../../api';
import { CheckField, FormField } from '../../components/FormField';
import { roleLabel } from '../../labels';
import type { SavedUser } from '../../types';

interface Draft {
  email: string;
  name: string;
  role: TenantRole;
  cardCode: string;
  active: boolean;
  password: string;
}

const ROLE_HINTS: Record<TenantRole, string> = {
  cliente_admin: 'Configura su portal: tolerancias, avisos, proveedores y usuarios.',
  compras: 'Ve todos los pedidos y aprueba lo que sale de tolerancia.',
  recepcion: 'Confirma la llegada física del material en la playa.',
  proveedor: 'Ve solo sus pedidos: los de su código de interlocutor en SAP.',
};

export function UserModal({
  slug,
  user,
  onClose,
  onSaved,
}: {
  slug: string;
  user: PortalUser | null;
  onClose: () => void;
  onSaved: (saved: SavedUser) => Promise<void>;
}): JSX.Element {
  const toast = useToast();
  const editing = user !== null;
  const [draft, setDraft] = useState<Draft>({
    email: user?.email ?? '',
    name: user?.name ?? '',
    role: user?.role ?? 'cliente_admin',
    cardCode: user?.cardCode ?? '',
    active: user?.active ?? true,
    password: '',
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]): void => setDraft((d) => ({ ...d, [key]: value }));

  const needsCard = draft.role === 'proveedor';
  const missing = !draft.email.trim() || !draft.name.trim() || (needsCard && !draft.cardCode.trim());

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const body = {
      email: draft.email.trim(),
      name: draft.name.trim(),
      role: draft.role,
      cardCode: needsCard ? draft.cardCode.trim() : null,
      active: draft.active,
      password: draft.password,
    };
    try {
      if (editing) {
        const res = await api.put<{ user: PortalUser }>(`${tenantPath(slug)}/users/${encodeURIComponent(user.id)}`, body);
        toast(`Usuario ${res.user.email} guardado`, 'ok');
        await onSaved({ user: res.user, invite: null });
      } else {
        const res = await api.post<SavedUser>(`${tenantPath(slug)}/users`, body);
        toast(`Usuario ${res.user.email} creado`, 'ok');
        await onSaved(res);
      }
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={editing ? `Editar ${user.name}` : 'Nuevo usuario'}
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button className="btn primary" onClick={() => void save()} disabled={busy || missing}>
            {busy ? 'Guardando…' : editing ? 'Guardar' : 'Crear usuario'}
          </button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (!missing) void save();
        }}
      >
        <div className="grid cols-2">
          <FormField label="Nombre">
            <input className="input" value={draft.name} autoComplete="off" onChange={(e) => set('name', e.target.value)} autoFocus />
          </FormField>
          <FormField label="Correo" hint="Es el usuario con el que entra en el portal.">
            <input className="input" type="email" autoComplete="off" value={draft.email} onChange={(e) => set('email', e.target.value)} />
          </FormField>
        </div>
        <div className="grid cols-2">
          <FormField label="Perfil" hint={ROLE_HINTS[draft.role]}>
            <select className="select" value={draft.role} onChange={(e) => set('role', e.target.value as TenantRole)}>
              {TENANT_ROLES.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
          </FormField>
          {needsCard ? (
            <FormField label="Código de proveedor (CardCode)" hint="El interlocutor de SAP. Tiene que estar habilitado en el portal del cliente.">
              <input className="input mono" value={draft.cardCode} spellCheck={false} placeholder="P00012" onChange={(e) => set('cardCode', e.target.value)} />
            </FormField>
          ) : (
            <div />
          )}
        </div>
        {editing ? null : (
          <div className="callout info">
            Al crearlo le llegará un correo de Provalia, desde noreply@ecinsa.com, con un enlace para que cree su propia contraseña.
          </div>
        )}
        {editing ? (
          <CheckField label="Activo" hint="Un usuario inactivo no puede entrar en el portal." checked={draft.active} onChange={(v) => set('active', v)} />
        ) : null}
        <ErrorBox error={error} />
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
